import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ArrowUpIcon, DeleteIcon, EyeIcon } from '@sketchnote/icons';
import * as React from 'react';
import {
  AlignType,
  ColorStyle,
  fills,
  TDShapeType,
  TDSnapshot,
  TDToolType,
  useTldrawApp,
} from 'sn-wb-tldraw';

import { CircleIcon } from '../Primitives/icons';
import { Alignment } from './Alignment';
import { colors, strokes } from './ColorMenu';
import { ColorSelector } from './ColorSelector';
import { EditorTools } from './EditorTools';
import { editorIcons } from './EditorTools';
import { stickyColors } from './NoteMenu';
import { SizeSelector, strokeSize } from './SizeSelector';

interface SelectionToolsProps {
  currentColor: ColorStyle;
  selectedShape: any;
  isFilled: boolean;
  currentSize: string;
}

const Shapes: TDToolType[] = [
  TDShapeType.Ellipse,
  TDShapeType.Rectangle,
  TDShapeType.Line,
  TDShapeType.Triangle,
];

const StrokeShapes: TDToolType[] = [...Shapes, TDShapeType.Draw];

const ColorShapes: TDToolType[] = [
  ...StrokeShapes,
  TDShapeType.Sticky,
  TDShapeType.Text,
  TDShapeType.Section,
  TDShapeType.Highlighter,
];

const DeleteShapes: TDShapeType[] = [TDShapeType.File, TDShapeType.Lesson, TDShapeType.Link];
const EditorShapes: TDShapeType[] = [TDShapeType.Text];

const currentStyleSelector = (s: TDSnapshot) => s.appState.currentStyle;
const activeToolSelector = (s: TDSnapshot) => s.appState.activeTool;

export const SelectionTools = React.memo(function SelectionTools({
  currentColor,
  selectedShape,
  isFilled,
  currentSize,
}: SelectionToolsProps): JSX.Element {
  const app = useTldrawApp();
  const currentStyle = app.useStore(currentStyleSelector);
  const activeTool = app.useStore(activeToolSelector);
  const selectedId = app.useStore((s) => s.document.pageStates.page.selectedIds);
  const hasMultipleSelectionSelector = selectedId.length > 1;

  const alignLeft = React.useCallback(() => {
    app.align(AlignType.Left);
  }, [app]);

  const incrementSize = () => {
    const scale = app.getShape(selectedId[0]).style.scale || 1;
    app.style({
      scale: scale + 0.1 * scale,
    });
  };
  const decrementSize = () => {
    const scale = app.getShape(selectedId[0]).style.scale || 1;
    app.style({
      scale: scale - 0.1 * scale,
    });
  };

  const isSticky = selectedShape === TDShapeType.Sticky;

  const seperator = (
    <hr className="transform rotate-90 w-13 translate-y-10 border-none h-1 bg-gray-800" />
  );
  return (
    <>
      {isSticky && (
        <div className="flex gap-10 items-center cursor-pointer">
          <div onClick={incrementSize}>{editorIcons['fontIncrement']}</div>
          <div onClick={decrementSize}>{editorIcons['fontDecrement']}</div>
          <div className=" text-gray-800">|</div>
        </div>
      )}
      {EditorShapes.includes(selectedShape) && (
        <>
          <EditorTools />
          <div className=" text-gray-800">|</div>
        </>
      )}
      {DeleteShapes.includes(selectedShape) && (
        <>
          <DeleteIcon fill="#fff" onClick={() => app.delete()} />
        </>
      )}
      {ColorShapes.includes(selectedShape) && (
        <>
          <DropdownMenu.Root dir="ltr">
            <DropdownMenu.Trigger asChild>
              <div className="flex items-center gap-2 cursor-pointer">
                <CircleIcon
                  wrapperClass="transform "
                  fill={isSticky ? stickyColors[currentColor] : colors[currentColor]}
                  stroke="white"
                />
                <ArrowUpIcon />
              </div>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
              className="flex flex-row items-center gap-2 justify-center bg-primary-900 rounded-sm"
              style={{ padding: '4px 8px', transform: 'translate(61px,-56px)' }}
            >
              <ColorSelector isSticky={isSticky} />
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </>
      )}

      {StrokeShapes.includes(selectedShape) && (
        <>
          {seperator}
          <DropdownMenu.Root dir="ltr">
            <DropdownMenu.Trigger asChild>
              <div className="flex justify-center items-center w-12">{strokeSize[currentSize]}</div>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content
              className="flex flex-row items-center justify-center bg-primary-900 rounded-sm  "
              style={{ padding: '4px 8px', transform: 'translate(14px,-56px)' }}
            >
              <SizeSelector />
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        </>
      )}
      {Shapes.includes(selectedShape) && (
        <>
          {seperator}
          <div
            className="flex justify-start items-center cursor-pointer"
            onClick={() => app.style({ isFilled: !isFilled })}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.9237 5.76655C11.8165 5.61991 9.26224 2.17578 5.99994 2.17578C2.73765 2.17578 0.183235 5.61991 0.0761486 5.76641C-0.0253829 5.90554 -0.0253829 6.09423 0.0761486 6.23336C0.183235 6.38001 2.73765 9.82413 5.99994 9.82413C9.26224 9.82413 11.8165 6.37999 11.9237 6.23348C12.0254 6.09438 12.0254 5.90554 11.9237 5.76655ZM5.99994 9.03293C3.59691 9.03293 1.51564 6.74699 0.899533 5.99969C1.51484 5.25173 3.59176 2.96699 5.99994 2.96699C8.40285 2.96699 10.484 5.25252 11.1004 6.00023C10.485 6.74817 8.40813 9.03293 5.99994 9.03293Z"
                fill="white"
              />
              <path
                d="M5.99992 3.62646C4.69112 3.62646 3.62628 4.6913 3.62628 6.0001C3.62628 7.3089 4.69112 8.37374 5.99992 8.37374C7.30872 8.37374 8.37356 7.3089 8.37356 6.0001C8.37356 4.6913 7.30872 3.62646 5.99992 3.62646ZM5.99992 7.58251C5.12734 7.58251 4.41751 6.87266 4.41751 6.0001C4.41751 5.12755 5.12736 4.41769 5.99992 4.41769C6.87248 4.41769 7.58233 5.12755 7.58233 6.0001C7.58233 6.87266 6.8725 7.58251 5.99992 7.58251Z"
                fill="white"
              />
            </svg>
          </div>
        </>
      )}
      {hasMultipleSelectionSelector && (
        <>
          {seperator}
          <div className="flex items-center justify-center">
            <Alignment />
          </div>
        </>
      )}
    </>
  );
});
